"use client";

import { useEffect, useRef } from "react";

const differentiators = [
  {
    number: "01",
    title: "We\'re Local — Actually Local",
    description: "Our team lives in Panama City Beach. We know the market, the contractors, the seasonal patterns, and the guests. You won't get a call center — you'll get a neighbor who knows your property.",
    icon: "🏖️",
  },
  {
    number: "02",
    title: "Hotel-Grade Turnover & Property Care",
    description: "Our 47-point cleaning checklist sets the baseline — but every turnover also includes a routine maintenance inspection, quality supply check and restock, appliance and device functionality check (batteries replaced on schedule), and a full concierge setup personalized for each guest arrival. Every visit is photo-documented.",
    icon: "✨",
  },
  {
    number: "03",
    title: "Transparent Reporting",
    description: "Monthly owner reports with full revenue breakdown, occupancy stats, guest feedback, and expense summaries. You always know exactly what's happening with your investment.",
    icon: "📊",
  },
  {
    number: "04",
    title: "No Lock-In Contracts",
    description: "We earn your business every month. Cancel anytime with 30 days notice. We're confident enough in our results that we don't need to trap you in a contract.",
    icon: "🤝",
  },
  {
    number: "05",
    title: "Revenue-First Mindset",
    description: "Our dynamic pricing strategy analyzes hundreds of data points daily — local events, competitor rates, demand patterns — to maximize your revenue. Average clients see 20–35% revenue lift.",
    icon: "💰",
  },
  {
    number: "06",
    title: "24/7 Guest Support",
    description: "Guests don't have business hours. Neither do we. Every message, maintenance request, and emergency is handled promptly and professionally — protecting your rating and your reputation.",
    icon: "🌙",
  },
];

export default function WhyChooseUs() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("revealed"); }); },
      { threshold: 0.08 }
    );
    const els = ref?.current?.querySelectorAll(".reveal-hidden");
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={ref} className="section-padding bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center mb-16 reveal-hidden">
          <span className="text-gold text-xs font-bold uppercase tracking-widest block mb-3">Why Premier</span>
          <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-5">
            Why PCB Hosts{" "}
            <span className="italic font-light text-gradient-gold">Choose Us</span>
          </h2>
          <p className="text-navy/55 text-lg max-w-2xl mx-auto">
            There are dozens of property management companies in PCB. Here&apos;s what makes Premier the one that owners recommend.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {differentiators?.map((item, i) => (
            <div
              key={item?.number}
              className="reveal-hidden group p-7 rounded-3xl border border-sand-medium bg-sand hover:border-gold/30 hover:shadow-luxury transition-all duration-400"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="text-3xl">{item?.icon}</div>
                <span className="text-gold/40 font-heading text-3xl font-bold group-hover:text-gold/60 transition-colors">
                  {item?.number}
                </span>
              </div>
              <h3 className="font-heading text-xl text-navy mb-3">{item?.title}</h3>
              <p className="text-navy/55 text-sm leading-relaxed">{item?.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}